import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Download, Trash2, Lock, Eye, Database, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PrivacySettings = () => {
  const { user, logout } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const response = await api.get('/privacy/policy');
      setPolicy(response.data);
    } catch (error) {
      console.error('Error fetching policy:', error);
    }
    setLoading(false);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await api.get('/privacy/export');
      const data = response.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diawise-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Data Exported Successfully",
        description: `Downloaded ${data.total_records.glucose_logs} glucose logs, ${data.total_records.meals} meals, and more.`
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Unable to export your data. Please try again.",
        variant: "destructive"
      });
    }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/privacy/delete-account');
      toast({
        title: "Account Deleted",
        description: "Your account and all data have been permanently deleted."
      });
      logout();
    } catch (error) {
      toast({
        title: "Deletion Failed",
        description: "Unable to delete your account. Please try again.",
        variant: "destructive"
      });
    }
    setDeleting(false);
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Shield className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-3xl font-bold">Privacy & Security</h2>
            <p className="text-emerald-100 text-lg">Your data, your control</p>
          </div>
        </div>
      </div>

      {/* Security Status */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Lock className="w-5 h-5 text-emerald-500" />
            Security Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-400">Encrypted Connection</p>
                <p className="text-xs text-green-600 dark:text-green-500">HTTPS enabled</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-400">Data Encrypted</p>
                <p className="text-xs text-green-600 dark:text-green-500">AES-256 at rest</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-400">Secure Auth</p>
                <p className="text-xs text-green-600 dark:text-green-500">JWT tokens</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Data Rights */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Your Data Rights</CardTitle>
          <CardDescription className="dark:text-gray-400">Control your personal health information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export Data */}
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <Download className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Export Your Data</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Download all your health data in JSON format</p>
              </div>
            </div>
            <Button 
              onClick={handleExportData} 
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {exporting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>

          {/* Delete Account */}
          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Delete Account</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Permanently delete your account and all data</p>
              </div>
            </div>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </DialogTrigger>
              <DialogContent className="dark:bg-gray-800">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Delete Account Permanently?
                  </DialogTitle>
                  <DialogDescription className="dark:text-gray-400">
                    This action cannot be undone. All your data including glucose logs, meals, medications, 
                    and health records will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>
                <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    <strong>Warning:</strong> You will lose access to:
                  </p>
                  <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 mt-2">
                    <li>All glucose readings and trends</li>
                    <li>Meal and nutrition history</li>
                    <li>Medication schedules</li>
                    <li>AI-generated insights</li>
                    <li>Your account and profile</li>
                  </ul>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Policy */}
      {policy && (
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="dark:text-white">Privacy Policy</CardTitle>
              <Badge variant="outline">v{policy.version}</Badge>
            </div>
            <CardDescription className="dark:text-gray-400">
              Last updated: {policy.last_updated}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Data We Collect */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data We Collect
              </h4>
              <ul className="space-y-1">
                {policy.data_collected.map((item, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* How We Use Your Data */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                How We Use Your Data
              </h4>
              <ul className="space-y-1">
                {policy.data_usage.map((item, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Sharing */}
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <h4 className="font-semibold text-emerald-800 dark:text-emerald-400 mb-1">Data Sharing</h4>
              <p className="text-sm text-emerald-700 dark:text-emerald-300">{policy.data_sharing}</p>
            </div>

            {/* Your Rights */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Your Rights (GDPR)</h4>
              <ul className="space-y-1">
                {policy.your_rights.map((item, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact */}
      <Card className="shadow-md border-0 bg-gray-50 dark:bg-gray-800">
        <CardContent className="py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Questions about your privacy? Contact us at{' '}
            <a href="mailto:privacy@diawise.app" className="text-emerald-600 dark:text-emerald-400 hover:underline">
              privacy@diawise.app
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacySettings;
